import React from 'react';
import { FilterState } from '../types';

interface FilterPanelProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  onAddressSearch: (address: string) => void;
  isSearching?: boolean;
  schoolCount: {
    elementary: number;
    middle: number;
    high: number;
    total: number;
  };
  currentAssignment?: {
    lat: number;
    lng: number;
    address?: string;
    elementary?: string;
    middle?: string;
    high?: string;
    drivingTimes?: Array<{
      schoolName: string;
      durationText: string;
      distanceText: string;
      isEstimate?: boolean;
    }>;
  } | null;
  onClearAssignment?: () => void;
  onMobileClose?: () => void;
}

export default function FilterPanel({
  filters,
  onFiltersChange,
  onAddressSearch,
  isSearching = false,
  schoolCount,
  currentAssignment,
  onClearAssignment,
  onMobileClose
}: FilterPanelProps) {
  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const address = formData.get('address') as string;
    if (address.trim()) {
      onAddressSearch(address.trim());
      // Close mobile panel after search to show results on map
      onMobileClose?.();
    }
  };

  const handleToggleFilter = (filterKey: keyof FilterState) => {
    if (typeof filters[filterKey] === 'boolean') {
      onFiltersChange({
        ...filters,
        [filterKey]: !filters[filterKey]
      });
    }
  };

  const handleSearchTermChange = (searchTerm: string) => {
    onFiltersChange({
      ...filters,
      searchTerm
    });
  };

  const handleClearFilters = () => {
    onFiltersChange({
      showElementary: true,
      showMiddle: true,
      showHigh: true,
      searchTerm: ''
    });
  };

  const activeFiltersCount = 
    (filters.showElementary ? 1 : 0) + 
    (filters.showMiddle ? 1 : 0) + 
    (filters.showHigh ? 1 : 0);

  return (
    <div className="w-80 bg-white shadow-lg border-r border-gray-200 flex flex-col h-full">
      {/* Fixed Header */}
      <div className="flex-shrink-0 p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-bold text-gray-800">Frisco ISD School Finder</h2>
          {/* Mobile close button */}
          {onMobileClose && (
            <button
              onClick={onMobileClose}
              className="md:hidden p-1 rounded-full hover:bg-gray-200 transition-colors"
              aria-label="Close menu"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <p className="text-sm text-gray-600">
          Find your assigned schools by address or explore the {schoolCount.total} schools in the district.
        </p>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto">
        {/* Address Search */}
        <div className="p-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-700 mb-3">Address Search</h3>
        <form onSubmit={handleSearchSubmit} className="space-y-3">
          <div>
            <input
              type="text"
              name="address"
              placeholder="Enter your address..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isSearching}
            />
          </div>
          <button
            type="submit"
            disabled={isSearching}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSearching ? 'Searching...' : 'Find My Schools'}
          </button>
        </form>
      </div>

      {/* School Assignment Results */}
      {currentAssignment && (
        <div className="p-4 border-b border-gray-200 bg-blue-50">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-700 flex items-center space-x-2">
              <img 
                src="https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png" 
                alt="Location marker"
                className="w-4 h-6"
              />
              <span>School Assignment</span>
            </h3>
            <button
              onClick={onClearAssignment}
              className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-200 transition-colors"
              title="Close school assignment"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="text-sm text-gray-600 mb-3">
            {currentAssignment.address ? (
              <p><strong>Location:</strong> {currentAssignment.address}</p>
            ) : (
            <p>Coordinates: {currentAssignment.lat.toFixed(6)}, {currentAssignment.lng.toFixed(6)}</p>
            )}
            {!currentAssignment.drivingTimes && (
              <p className="text-xs text-blue-600 flex items-center space-x-1 mt-1">
                <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Calculating driving times...</span>
              </p>
            )}
          </div>
          
          <div className="space-y-3">
            {currentAssignment.elementary ? (
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="text-sm font-medium text-gray-700">
                    <strong>Elementary:</strong> {currentAssignment.elementary}
                  </span>
                </div>
                {currentAssignment.drivingTimes && currentAssignment.drivingTimes.find(dt => dt.schoolName === currentAssignment.elementary) && (
                  <div className="ml-5 text-xs text-gray-600 flex items-center space-x-1">
                    <span>🚗</span>
                    <span>
                      {currentAssignment.drivingTimes.find(dt => dt.schoolName === currentAssignment.elementary)?.durationText} 
                      ({currentAssignment.drivingTimes.find(dt => dt.schoolName === currentAssignment.elementary)?.distanceText})
                      {currentAssignment.drivingTimes.find(dt => dt.schoolName === currentAssignment.elementary)?.isEstimate && (
                        <span className="text-gray-500 ml-1">*est.</span>
                      )}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-2 text-gray-500">
                <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                <span className="text-sm">
                  <em>Elementary: Not found</em>
                </span>
              </div>
            )}

            {currentAssignment.middle ? (
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-sm font-medium text-gray-700">
                    <strong>Middle:</strong> {currentAssignment.middle}
                  </span>
                </div>
                {currentAssignment.drivingTimes && currentAssignment.drivingTimes.find(dt => dt.schoolName === currentAssignment.middle) && (
                  <div className="ml-5 text-xs text-gray-600 flex items-center space-x-1">
                    <span>🚗</span>
                    <span>
                      {currentAssignment.drivingTimes.find(dt => dt.schoolName === currentAssignment.middle)?.durationText} 
                      ({currentAssignment.drivingTimes.find(dt => dt.schoolName === currentAssignment.middle)?.distanceText})
                      {currentAssignment.drivingTimes.find(dt => dt.schoolName === currentAssignment.middle)?.isEstimate && (
                        <span className="text-gray-500 ml-1">*est.</span>
                      )}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-2 text-gray-500">
                <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                <span className="text-sm">
                  <em>Middle: Not found</em>
                </span>
              </div>
            )}

            {currentAssignment.high ? (
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                  <span className="text-sm font-medium text-gray-700">
                    <strong>High:</strong> {currentAssignment.high}
                  </span>
                </div>
                {currentAssignment.drivingTimes && currentAssignment.drivingTimes.find(dt => dt.schoolName === currentAssignment.high) && (
                  <div className="ml-5 text-xs text-gray-600 flex items-center space-x-1">
                    <span>🚗</span>
                    <span>
                      {currentAssignment.drivingTimes.find(dt => dt.schoolName === currentAssignment.high)?.durationText} 
                      ({currentAssignment.drivingTimes.find(dt => dt.schoolName === currentAssignment.high)?.distanceText})
                      {currentAssignment.drivingTimes.find(dt => dt.schoolName === currentAssignment.high)?.isEstimate && (
                        <span className="text-gray-500 ml-1">*est.</span>
                      )}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-2 text-gray-500">
                <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                <span className="text-sm">
                  <em>High: Not found</em>
                </span>
              </div>
            )}
          </div>
          
          {currentAssignment.drivingTimes && currentAssignment.drivingTimes.some(dt => dt.isEstimate) && (
            <p className="text-xs text-gray-500 mt-3 italic">
              *est. = estimated time based on straight-line distance
            </p>
          )}
          
          <p className="text-xs text-gray-500 mt-2">
            Click anywhere else on the map to search a new location
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-700">Filter Schools</h3>
          {activeFiltersCount < 3 && (
            <button
              onClick={handleClearFilters}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Show All
            </button>
          )}
        </div>

        <div className="space-y-3">
          {/* Elementary Toggle */}
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.showElementary}
              onChange={() => handleToggleFilter('showElementary')}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded-full bg-blue-500"></div>
              <span className="text-sm font-medium text-gray-700">
                Elementary ({schoolCount.elementary})
              </span>
            </div>
          </label>

          {/* Middle Toggle */}
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.showMiddle}
              onChange={() => handleToggleFilter('showMiddle')}
              className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
            />
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded-full bg-green-500"></div>
              <span className="text-sm font-medium text-gray-700">
                Middle ({schoolCount.middle})
              </span>
            </div>
          </label>

          {/* High Toggle */}
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.showHigh}
              onChange={() => handleToggleFilter('showHigh')}
              className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
            />
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded-full bg-orange-500"></div>
              <span className="text-sm font-medium text-gray-700">
                High ({schoolCount.high})
              </span>
            </div>
          </label>
        </div>
      </div>

      {/* School Search */}
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-700 mb-3">Find School</h3>
        <input
          type="text"
          placeholder="Search by school name..."
          value={filters.searchTerm}
          onChange={(e) => handleSearchTermChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        {filters.searchTerm && (
          <button
            onClick={() => handleSearchTermChange('')}
            className="mt-2 text-sm text-gray-500 hover:text-gray-700"
          >
            Clear search
          </button>
        )}
      </div>

        {/* Instructions */}
        <div className="p-4">
          <h3 className="text-lg font-semibold text-gray-700 mb-3">How to Use</h3>
          <div className="space-y-2 text-sm text-gray-600">
            <p>• <strong>Click anywhere</strong> on the map to see school assignments</p>
            <p>• <strong>Search an address</strong> to find specific school zones</p>
            <p>• <strong>Click school markers</strong> for detailed information</p>
            <p>• <strong>Hover over zones</strong> to highlight boundaries</p>
            <p>• <strong>Use filters</strong> to show/hide school types</p>
          </div>
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-gray-50">
        <p className="text-xs text-gray-500 text-center">
          Frisco ISD School Zone Map<br/>
          Data for 2025-26 School Year
        </p>
      </div>
    </div>
  );
} 